import { useId, type MouseEvent, type ReactElement, type Ref } from "react";
import { classes } from "./classes.ts";
import { Glyph } from "./Glyph.tsx";
import { useInteraction } from "./interaction.tsx";
import { KIT_METRICS, logical } from "./metrics.ts";
import { fieldFrame, frameClass } from "./state.ts";

/**
 * A text field (brief section 5.1; ADR-061's amendment "the kit and the entry forms match the design"): a
 * pixel-font label, a framed box whose input sets Noto Sans, and under it, only when there is one, a note line
 * holding a hint or, when its input has failed, an error led by the 8 px alert glyph. With neither the field
 * ends at its box, so an error appearing moves what is below the field, as the design draws it; a note is one
 * caption line, and a longer one wraps. A field whose error can appear as the keyboard leaves it, while the
 * pointer is already going down on a control under it, asks to `reserveNote`: the line keeps its space while
 * empty, so the sentence appearing moves nothing and the press lands where it was aimed. The note line describes the input and is a polite live region, which
 * is why its element stays on the page, empty and with no height, when there is no note: a sentence that
 * appears while the input keeps focus, as a refusal does after Enter in that field, is read only if the
 * region was already there, since focusing an input that already has focus fires no event a screen reader
 * reads. With a limit the input stops at
 * it; a field whose limit the player needs to see asks for the counter, which counts the characters beside
 * the label (a character name's 24), and one whose limit no player types to (an email's 254, a password's
 * 200) leaves it out. `required`, `minLength` and `pattern` are the input's own constraints. A focused field
 * draws the aether focus frame; the input router gives it the keyboard as it takes focus (CLIENT.md 7.3), so
 * every key types and the game moves nothing.
 *
 * A password field can be shown (ADR-061's amendment "the password is typed twice to create an account, and
 * can be shown"): `revealed` draws what it holds as text, and `reveal` adds the design's button inside the
 * box at its right edge, an eye that becomes a closed eye while the password is shown. The field holds no
 * reveal state of its own, so two fields can share one. The button follows the input in the page, so Tab
 * reaches it next; it is a `type="button"`, so it never submits the field's form; its name says what a press
 * will do and changes with the state, so it carries no `aria-pressed`, which would have a screen reader say
 * "Hide password, pressed"; and a pointer press on it leaves the keyboard focus where it was.
 */

/**
 * A password field's show or hide button: its accessible name, which says what a press will do ("Show
 * password" while hidden, "Hide password" while shown), what a press does, and whether it is disabled.
 */
export interface FieldReveal {
  readonly label: string;
  readonly onToggle: () => void;
  readonly disabled?: boolean;
}

/**
 * Keeps the keyboard focus where it is when the pointer presses the reveal button.
 *
 * @param event The mouse press.
 */
function keepFocus(event: MouseEvent<HTMLButtonElement>): void {
  event.preventDefault();
}

/**
 * The show or hide button of a password field, drawn as a link holding the eye glyph.
 *
 * @param props The button's `reveal`, and whether the password is shown now, which picks the glyph.
 * @returns The button.
 */
function RevealButton({ reveal, shown }: { readonly reveal: FieldReveal; readonly shown: boolean }): ReactElement {
  const disabled = reveal.disabled === true;
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const box = KIT_METRICS.field.reveal;
  return (
    <button
      type="button"
      className={classes("ot-link", "ot-field__reveal", state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus", disabled && "is-disabled")}
      data-kit="field-reveal"
      disabled={disabled}
      aria-label={reveal.label}
      onClick={reveal.onToggle}
      onMouseDown={keepFocus}
      style={{ width: logical(box.box), height: logical(box.box), padding: logical(box.glyphOffset), marginRight: logical(box.right) }}
      {...handlers}
    >
      <Glyph name={shown ? "eye-closed" : "eye"} />
    </button>
  );
}

/**
 * A text field.
 *
 * @param props The label; the input's `type`; its `value` and `onChange`; a `hint`; an `error`, which
 *   replaces the hint and marks the input invalid; `maxLength`, which stops the typing; `counter`, which
 *   counts the characters against `maxLength` beside the label and shows nothing without one; `required`,
 *   `minLength` and `pattern`, the input's constraints for its form; `disabled`; `name` and `autoComplete`
 *   for the browser's form filling; `autoFocus`; `inputRef`; `revealed`, which draws a password as text;
 *   `reveal`, a password field's show or hide button; `reserveNote`, which keeps the note line's space while
 *   it holds nothing; and `onLeave`, called when the keyboard leaves the input.
 * @returns The field.
 * @example
 * <TextField label="Password" type="password" value={password} onChange={setPassword} revealed={shown} reveal={{ label: shown ? "Hide password" : "Show password", onToggle: toggle }} />
 */
export function TextField({ label, type = "text", value, onChange, hint, error, maxLength, counter = false, required, minLength, pattern, disabled = false, name, autoComplete, autoFocus, inputRef, revealed = false, reveal, reserveNote = false, onLeave }: { readonly label: string; readonly type?: "text" | "password" | "email"; readonly value: string; readonly onChange: (value: string) => void; readonly hint?: string; readonly error?: string; readonly maxLength?: number; readonly counter?: boolean; readonly required?: boolean; readonly minLength?: number; readonly pattern?: string; readonly disabled?: boolean; readonly name?: string; readonly autoComplete?: string; readonly autoFocus?: boolean; readonly inputRef?: Ref<HTMLInputElement>; readonly revealed?: boolean; readonly reveal?: FieldReveal; readonly reserveNote?: boolean; readonly onLeave?: () => void }): ReactElement {
  const id = useId();
  const noteId = `${id}-note`;
  const { state, handlers } = useInteraction({ disabled, focus: "any" });
  const failed = error !== undefined;
  const frame = fieldFrame({ ...state, disabled, error: failed });
  const note = failed ? error : hint;
  return (
    <div className={classes("ot-field", state.focus && "is-focus", disabled && "is-disabled", failed && "is-error")} data-kit="field">
      <div className="ot-field__head" data-kit="field-head" style={{ height: logical(KIT_METRICS.field.head) }}>
        <label className="ot-field__label" htmlFor={id}>
          {label}
        </label>
        {counter && maxLength !== undefined ? (
          <span className={classes("ot-field__count", value.length > maxLength && "is-over")} aria-hidden="true">
            {`${value.length}/${maxLength}`}
          </span>
        ) : null}
      </div>
      <div className={classes("ot-field__box", reveal !== undefined && "ot-field__box--reveal", frameClass(frame))} data-kit="field-box" style={{ height: logical(KIT_METRICS.field.boxHeight), marginTop: logical(KIT_METRICS.field.headGap) }} onPointerEnter={handlers.onPointerEnter} onPointerLeave={handlers.onPointerLeave} onPointerCancel={handlers.onPointerCancel}>
        <input
          ref={inputRef}
          id={id}
          className="ot-field__input"
          type={type === "password" && revealed ? "text" : type}
          name={name}
          value={value}
          maxLength={maxLength}
          required={required}
          minLength={minLength}
          pattern={pattern}
          disabled={disabled}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          spellCheck={false}
          aria-invalid={failed ? true : undefined}
          aria-describedby={note === undefined ? undefined : noteId}
          onChange={(event) => onChange(event.currentTarget.value)}
          onFocus={handlers.onFocus}
          onBlur={(event) => {
            handlers.onBlur(event);
            onLeave?.();
          }}
          style={{ padding: `${logical(KIT_METRICS.field.paddingY)} ${logical(KIT_METRICS.field.paddingX)}` }}
        />
        {reveal === undefined ? null : <RevealButton reveal={reveal} shown={revealed} />}
      </div>
      <div className={failed ? "ot-field__error" : "ot-field__hint"} id={noteId} data-kit="field-note" style={note === undefined && !reserveNote ? undefined : { minHeight: logical(KIT_METRICS.note.height), marginTop: logical(KIT_METRICS.field.noteGap), gap: logical(KIT_METRICS.note.gap) }} aria-live="polite">
        {failed ? (
          <span className="ot-field__mark" style={{ marginTop: logical(KIT_METRICS.note.glyphOffset) }}>
            <Glyph name="alert" />
          </span>
        ) : null}
        {note === undefined ? null : (
          <span className="ot-field__note" style={{ marginTop: logical(KIT_METRICS.note.textOffset) }}>
            {note}
          </span>
        )}
      </div>
    </div>
  );
}
